/* Кнопка экрана создания (APP-SPEC-5 §1.6, BotCore.foot): стоит всегда, «Создать … · N C» (до файла — «от N C»);
   пока чего-то не хватает — неактивна с той же подписью, нажатие говорит чего (OffButton); пока отправляется — «Создаём» с крутилкой.
   Circle не хватает (баланс GET /app/balance меньше цены) — «Не хватает N C · Пополнить» и лист «Не хватает Circle» (BotCore.poor):
   «Сделать дешевле на 10%, с водяным знаком» — только если форма публикует скидку (cheap), иначе строка «Для этой работы скидки нет»;
   белая «Пополнить» ведёт в «Баланс». Сумм за пределами ответа сервера кнопка не придумывает. */
import { useRef, useState } from 'react';
import { useApPad } from '../../../ui/use-ap-pad';
import { uiFormat, uiText } from '../../../i18n';
import { OffButton } from '../../../ui/OffButton';
import { Sheet, SheetHead } from '../../../ui/Sheet';
import { cNum } from './order-tail-model';
import { ToolIcon } from './ToolIcon';
import { toolPriceLabel, useCircleBalance } from './tool-helpers';

export interface ToolCreateButtonProps {
  label: string;
  /** Итог на кнопке; null — цена неизвестна, без суммы */
  price: number | null;
  /** «от N C» — итог зависит от длины файла, а файла ещё нет */
  from?: boolean;
  /** Платная ли работа (form.chargeable): у бесплатной сумма не пишется */
  chargeable?: boolean;
  missing: string | null;
  busy: boolean;
  /** busy из-за загрузки файла: «Загружаем файл», а не «Создаём» (заказ ещё не отправляется) */
  uploading?: boolean;
  blocked: boolean;
  /** Есть сохранённая заявка: кнопка продолжает проверку того же ключа */
  pending: boolean;
  available: boolean;
  onCreate(): void;
  /** Скидка «Сделать дешевле»: цена со скидкой и создание со скидкой (только если её публикует форма) */
  cheap?: { price: number; where: string; go(): void } | null;
  className?: string;
}
export function ToolCreateButton(props: ToolCreateButtonProps) {
  const { price, chargeable = true } = props;
  const balance = useCircleBalance(chargeable && price !== null && price > 0);
  const [poorOpen, setPoorOpen] = useState(false);
  const poor = chargeable && !props.from && price !== null && balance !== null && balance < price && !props.pending;
  const label = toolPriceLabel(props.label, price, props.from, chargeable);
  const miss = props.blocked
    ? uiText('Проверяем доступность')
    : props.pending
      ? undefined
      : props.missing
        ? uiText(props.missing)
        : !props.available
          ? uiText('Создание сейчас недоступно')
          : undefined;
  const cheapOk = !!props.cheap && balance !== null && props.cheap.price <= balance;
  const footRef = useRef<HTMLDivElement>(null);
  useApPad(footRef);
  return (
    <div ref={footRef} className={`ap-foot studio-native-foot${props.className ? ' ' + props.className : ''}`}>
      {props.busy ? (
        <button className="btn" type="button" aria-busy="true">
          <span className="sp" />
          {uiText(props.uploading ? 'Загружаем файл' : 'Создаём')}
        </button>
      ) : poor && !miss ? (
        <button className="btn fin" type="button" onClick={() => setPoorOpen(true)}>
          {uiFormat('Не хватает {0} C · Пополнить', cNum(price! - balance!))}
        </button>
      ) : (
        <OffButton
          className="fin"
          label={props.pending ? uiText('Продолжить проверку') : label}
          miss={miss}
          onClick={() => {
            if (!props.busy && !props.blocked && (props.pending || (!props.missing && props.available))) props.onCreate();
          }}
        />
      )}
      {poorOpen && price !== null && balance !== null && (
        <Sheet id="bcPoorNative" label={uiText('Не хватает Circle')} open onClose={() => setPoorOpen(false)}>
          <div className="cbody bc-poor">
            <SheetHead
              title={uiFormat('Не хватает {0} C', cNum(Math.max(0, price - balance)))}
              sub={uiFormat('На балансе {0} C, нужно {1} C', cNum(balance), cNum(price))}
              onClose={() => setPoorOpen(false)}
            />
            {props.cheap ? (
              <OffButtonRow
                ok={cheapOk}
                title={uiText('Сделать дешевле на 10%, с водяным знаком')}
                sub={
                  cheapOk
                    ? uiFormat('Создать за {0} C вместо {1} C', cNum(props.cheap.price), cNum(price))
                    : uiFormat('Со скидкой всё равно не хватает {0} C', cNum(props.cheap.price - balance))
                }
                onClick={() => {
                  setPoorOpen(false);
                  props.cheap!.go();
                }}
              />
            ) : (
              <OffButtonRow ok={false} title={uiText('Сделать дешевле на 10%, с водяным знаком')} sub={uiText('Для этой работы скидки нет')} />
            )}
            <div className="cta">
              <button
                className="btn fin"
                type="button"
                onClick={() => {
                  setPoorOpen(false);
                  location.hash = '#balance';
                }}
              >
                {uiText('Пополнить')}
              </button>
            </div>
          </div>
        </Sheet>
      )}
    </div>
  );
}
function OffButtonRow({ ok, title, sub, onClick }: { ok: boolean; title: string; sub: string; onClick?(): void }) {
  return (
    <button
      className={`bc-po${ok ? '' : ' off'}`}
      type="button"
      aria-disabled={ok ? undefined : 'true'}
      onClick={() => {
        if (ok) onClick?.();
      }}
    >
      <span className="ic">%</span>
      <span className="m">
        <b>{title}</b>
        <span>{sub}</span>
      </span>
      <span className="rt">
        <ToolIcon kind="chev" />
      </span>
    </button>
  );
}
